import React from 'react';

/**
 * Skeleton do painel de turma (Panel) e da aula particular (PanelPrivate).
 *
 * <p><b>Auto-contido:</b> inline styles + tokens globais (Shell.css é
 * eager-loaded). NÃO reusa `.pnl-*` do Panel.css porque essa CSS é lazy —
 * quando este skeleton renderiza como Suspense fallback (primeira visita à
 * rota), o chunk do Panel ainda não chegou; classes viriam sem estilo.
 *
 * <p><b>Anatomia — na ORDEM DA PÁGINA</b> (§1 REDLINE-painel-tema, 16/08):
 * card de tema → faixa de eventos → stats → tabela.
 *
 * <p>🔴 <b>A ordem importa mais que o desenho.</b> Skeleton existe pra reservar
 * o lugar do que vem; desenhando a ordem ANTIGA, ele prometia stats no topo e
 * a página saltava quando o dado chegava — que é exatamente o "flash of
 * misplaced content" que ele deveria impedir. Quando a estrutura muda, o
 * skeleton muda junto ou vira mentira.
 *
 * <p>⚠️ <b>Não inclui</b> a `.pnl-wktoolbar` (navegador + Atualizar) nem o card
 * de convites: os dois só aparecem depois do primeiro load, e o skeleton é pra
 * primeiro load.
 */
export default function PanelSkeleton({ statCards = 4, rosterRows = 4 }) {
	return (
		<div aria-busy="true" aria-live="polite">
			{/* Card de tema — rótulo + título, com o chip à direita. */}
			<div
				aria-hidden="true"
				style={{
					background: 'var(--paper)',
					border: '1px solid var(--ink-100)',
					borderRadius: 12,
					padding: '14px 16px',
					marginBottom: 14,
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<span className="sk" style={{ width: 96, height: 9 }} />
					<span style={{ flex: 1 }} />
					<span className="sk" style={{ width: 62, height: 16, borderRadius: 999 }} />
				</div>
				<span className="sk" style={{ width: '52%', height: 16, marginTop: 8, display: 'block' }} />
			</div>

			{/* Faixa de eventos — cabeçalho + 2 linhas com a coluna de hora fixa. */}
			<div
				aria-hidden="true"
				style={{
					background: 'var(--paper)',
					border: '1px solid var(--ink-100)',
					borderRadius: 14,
					overflow: 'hidden',
					marginBottom: 18,
				}}
			>
				<div style={{ padding: '11px 16px', borderBottom: '1px solid var(--ink-100)' }}>
					<span className="sk" style={{ width: 128, height: 9, display: 'block' }} />
				</div>
				{[0, 1].map(i => (
					<div
						key={i}
						style={{
							display: 'flex', alignItems: 'center', gap: 12,
							padding: '11px 16px',
							borderBottom: i === 0 ? '1px solid var(--ink-50)' : 'none',
						}}
					>
						<span className="sk" style={{ width: 158, height: 12, flexShrink: 0 }} />
						<span className="sk skc" style={{ width: 24, height: 24, flexShrink: 0 }} />
						<span className="sk" style={{ width: `${38 + i * 14}%`, height: 13 }} />
					</div>
				))}
			</div>

			<div
				aria-hidden="true"
				style={{
					display: 'grid',
					gridTemplateColumns: `repeat(${statCards}, 1fr)`,
					gap: 13,
					marginBottom: 20,
				}}
			>
				{Array.from({ length: statCards }).map((_, i) => (
					<div
						key={i}
						style={{
							background: 'var(--paper)',
							border: '1px solid var(--ink-100)',
							borderRadius: 13,
							padding: '14px 15px',
						}}
					>
						<span className="sk" style={{ width: 42, height: 26, display: 'block' }} />
						<span className="sk" style={{ width: '68%', height: 10, marginTop: 8, display: 'block' }} />
					</div>
				))}
			</div>

			<div
				aria-hidden="true"
				style={{
					background: 'var(--paper)',
					border: '1px solid var(--ink-100)',
					borderRadius: 14,
					overflow: 'hidden',
				}}
			>
				{/* Thead */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						padding: '11px 18px',
						borderBottom: '1px solid var(--ink-100)',
					}}
				>
					<div style={{ flex: 1 }}>
						<span className="sk" style={{ width: 70, height: 9, display: 'block' }} />
					</div>
					<div style={{ width: 210 }}>
						<span className="sk" style={{ width: 60, height: 9, display: 'block' }} />
					</div>
					<div style={{ width: 116 }}>
						<span className="sk" style={{ width: 76, height: 9, display: 'block' }} />
					</div>
					<div style={{ width: 64, display: 'flex', justifyContent: 'center' }}>
						<span className="sk" style={{ width: 40, height: 9 }} />
					</div>
				</div>
				{/* Rows */}
				{Array.from({ length: rosterRows }).map((_, i) => {
					const isLast = i === rosterRows - 1;
					return (
						<div
							key={i}
							style={{
								display: 'flex',
								alignItems: 'center',
								padding: '12px 18px',
								borderBottom: isLast ? 'none' : '1px solid var(--ink-50)',
							}}
						>
							<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 11 }}>
								<span className="sk skc" style={{ width: 34, height: 34, flexShrink: 0 }} />
								<span className="sk" style={{ width: `${35 + (i % 3) * 8}%`, height: 14 }} />
							</div>
							<div style={{ width: 210, paddingRight: 20 }}>
								<span className="sk" style={{ width: '100%', height: 7, display: 'block' }} />
								<span className="sk" style={{ width: 70, height: 9, marginTop: 6, display: 'block' }} />
							</div>
							<div style={{ width: 116, display: 'flex', alignItems: 'flex-end', gap: 4, height: 20 }}>
								{[6, 11, 15, 18, 18, 11, 6].map((h, j) => (
									<span key={j} className="sk" style={{ width: 12, height: h, borderRadius: 2 }} />
								))}
							</div>
							<div style={{ width: 64, display: 'flex', justifyContent: 'center' }}>
								<span className="sk skc" style={{ width: 16, height: 16 }} />
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
